import { useState, useEffect } from 'react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import { Shipment } from '@/types';
import { Package, MapPin, Clock, CheckCircle, Truck, Search, Star } from 'lucide-react';
import LoadingSpinner from '@/components/UI/LoadingSpinner';

const LimitedTrackingMap = () => {
  const { user } = useAuth();
  const [shipments, setShipments] = useState<Shipment[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchCode, setSearchCode] = useState('');
  const [selectedShipment, setSelectedShipment] = useState<Shipment | null>(null);

  useEffect(() => {
    if (user) {
      fetchUserShipments();
    }
  }, [user]);

  const fetchUserShipments = async () => {
    if (!user) return;
    
    try {
      setLoading(true);
      const q = query(
        collection(db, 'shipments'),
        where('requesterId', '==', user.id)
      );
      const snapshot = await getDocs(q);
      const shipmentsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate() || new Date(),
        updatedAt: doc.data().updatedAt?.toDate() || new Date(),
        trackingUpdates: doc.data().trackingUpdates?.map((update: any) => ({
          ...update,
          timestamp: update.timestamp?.toDate() || new Date()
        })) || []
      } as Shipment));
      setShipments(shipmentsData);
    } catch (error) {
      console.error('Error fetching shipments:', error);
    } finally {
      setLoading(false);
    }
  };

  const searchByTrackingCode = async () => {
    if (!searchCode.trim()) return;

    try {
      setLoading(true);
      const q = query(
        collection(db, 'shipments'),
        where('trackingCode', '==', searchCode.toUpperCase().trim())
      );
      const snapshot = await getDocs(q);
      
      if (snapshot.empty) {
        alert('❌ No shipment found with that tracking code.');
        return;
      }

      const shipmentData = {
        id: snapshot.docs[0].id,
        ...snapshot.docs[0].data(),
        createdAt: snapshot.docs[0].data().createdAt?.toDate() || new Date(),
        updatedAt: snapshot.docs[0].data().updatedAt?.toDate() || new Date(),
        trackingUpdates: snapshot.docs[0].data().trackingUpdates?.map((update: any) => ({
          ...update,
          timestamp: update.timestamp?.toDate() || new Date()
        })) || []
      } as Shipment;

      setSelectedShipment(shipmentData);
    } catch (error) {
      console.error('Error searching shipment:', error);
      alert('Failed to search shipment');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'delivered':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'in_transit':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'assigned':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'pending':
        return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'cancelled':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'delivered':
        return <CheckCircle className="w-5 h-5" />;
      case 'in_transit':
        return <Truck className="w-5 h-5" />;
      case 'assigned':
      case 'pending':
        return <Clock className="w-5 h-5" />;
      default:
        return <Package className="w-5 h-5" />;
    }
  };

  const ShipmentCard = ({ shipment }: { shipment: Shipment }) => (
    <div 
      className="bg-white border border-gray-200 rounded-xl p-5 hover:shadow-lg transition-shadow cursor-pointer"
      onClick={() => setSelectedShipment(shipment)}
    >
      <div className="flex justify-between items-start mb-3">
        <div>
          <div className="font-mono font-bold text-lg text-gray-800">{shipment.trackingCode}</div>
          <div className="text-sm text-gray-500">
            Ordered {shipment.createdAt.toLocaleDateString()}
          </div>
        </div>
        <span className={`px-3 py-1.5 rounded-full text-xs font-medium border inline-flex items-center gap-1.5 ${getStatusColor(shipment.status)}`}>
          {getStatusIcon(shipment.status)}
          {shipment.status.replace('_', ' ').toUpperCase()}
        </span>
      </div>

      <div className="space-y-2 text-sm">
        <div className="flex items-start gap-2">
          <MapPin className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
          <div>
            <div className="text-gray-500 text-xs">From</div>
            <div className="text-gray-800">{shipment.pickupLocation.address}</div>
          </div>
        </div>
        <div className="flex items-start gap-2">
          <MapPin className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
          <div>
            <div className="text-gray-500 text-xs">To</div>
            <div className="text-gray-800">{shipment.dropoffLocation.address}</div>
          </div>
        </div>
        <div className="flex items-center gap-2 pt-2 border-t border-gray-100">
          <Package className="w-4 h-4 text-gray-400" />
          <div className="text-gray-700">{shipment.cargoWeightKg} kg</div>
          {shipment.assignedDriverName && (
            <>
              <span className="text-gray-300">•</span>
              <Truck className="w-4 h-4 text-gray-400" />
              <div className="text-gray-700">{shipment.assignedDriverName}</div>
            </>
          )}
        </div>
      </div>
    </div>
  );

  if (loading && !selectedShipment) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Search by Tracking Code */}
      <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-100">
        <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
          <Search size={20} />
          Track Your Shipment
        </h3>
        <div className="flex gap-3">
          <input
            type="text"
            placeholder="Enter tracking code (e.g., SHIP-ABC123)"
            className="flex-1 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
            value={searchCode}
            onChange={(e) => setSearchCode(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && searchByTrackingCode()}
          />
          <button
            onClick={searchByTrackingCode}
            className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold transition-colors"
          >
            Search
          </button>
        </div>
      </div>

      {/* Selected Shipment Details */}
      {selectedShipment && (
        <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-100">
          <div className="flex justify-between items-start mb-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-800">{selectedShipment.trackingCode}</h2>
              <p className="text-gray-500 mt-1">Shipment Details & Tracking</p>
            </div>
            <button
              onClick={() => setSelectedShipment(null)}
              className="text-gray-400 hover:text-gray-600"
            >
              ✕
            </button>
          </div>

          {/* Status Timeline */}
          <div className="mb-6">
            <h3 className="font-semibold text-gray-800 mb-3">Tracking History</h3>
            <div className="space-y-3">
              {selectedShipment.trackingUpdates.map((update, index) => (
                <div key={index} className="flex gap-3">
                  <div className="flex flex-col items-center">
                    <div className={`w-3 h-3 rounded-full ${index === 0 ? 'bg-blue-600' : 'bg-gray-300'}`}></div>
                    {index < selectedShipment.trackingUpdates.length - 1 && (
                      <div className="w-0.5 h-full bg-gray-200 my-1"></div>
                    )}
                  </div>
                  <div className="flex-1 pb-4">
                    <div className="text-sm font-medium text-gray-800">{update.note}</div>
                    <div className="text-xs text-gray-500">
                      {update.timestamp.toLocaleString()}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Driver Info */}
          {selectedShipment.assignedDriverName && (
            <div className="bg-gray-50 p-4 rounded-lg mb-6">
              <h3 className="font-semibold text-gray-800 mb-2">Assigned Driver</h3>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold">
                  {selectedShipment.assignedDriverName.charAt(0)}
                </div>
                <div>
                  <div className="font-medium text-gray-800">{selectedShipment.assignedDriverName}</div>
                  <div className="text-sm text-gray-500">{selectedShipment.assignedVehiclePlate || 'Vehicle assigned'}</div>
                </div>
              </div>
            </div>
          )}

          {/* Rate Delivery (if delivered and not rated) */}
          {selectedShipment.status === 'delivered' && !selectedShipment.driverRating && (
            <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg">
              <h3 className="font-semibold text-gray-800 mb-2 flex items-center gap-2">
                <Star size={18} className="text-yellow-600" />
                Rate Your Delivery
              </h3>
              <p className="text-sm text-gray-600 mb-3">
                How was your delivery experience? Your feedback helps us improve.
              </p>
              <button className="px-4 py-2 bg-yellow-600 hover:bg-yellow-700 text-white rounded-lg text-sm font-semibold transition-colors">
                Rate Now
              </button>
            </div>
          )}
        </div>
      )}

      {/* User's Shipments List */}
      {!selectedShipment && user && (
        <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-100">
          <h2 className="text-xl font-bold text-gray-800 mb-4">My Shipments</h2>
          {shipments.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Package size={48} className="mx-auto mb-3 text-gray-300" />
              <p>No shipments found</p>
              <p className="text-sm">Book your first delivery to get started</p>
            </div>
          ) : (
            <div className="grid gap-4 md:grid-cols-2">
              {shipments.map(shipment => (
                <ShipmentCard key={shipment.id} shipment={shipment} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default LimitedTrackingMap;
